import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import config from "@/config";
import AddIcon from "@/shared-components/icons/components/actions/AddIcon";
import PublicSuccessCheck from "@/shared-components/icons/components/public/PublicSuccessCheck";
import Loader from '@/shared-components/loader/Loader';
import Text from "@/shared-components/text/components/Text";

import "./ArticleRow.css";

/**
 * Props:
 *   article     = the full article object returned from the backend
 *   isSelected  = boolean (true if this article’s ID is already in the “selected” list)
 *   onToggle    = async function(id: string) to add/remove this article’s ID
 *   closePopup  = function() to close the bottom sheet (optional)
 *
 * This component:
 *   • Displays the cover image (if any) or a placeholder.  
 *   • Shows title (italic, ellipsized).  
 *   • When isSelected===true, renders a “✅” and a background highlight.  
 *   • Otherwise renders a “+” icon.  
 *   • Clicking the icon toggles selection (spinner appears briefly while toggling).  
 *   • Only the “+” / “✓” icon is clickable.  Clicking the row itself no longer navigates.
 */
export default function ArticleRow({
  article,
  isSelected,
  onToggle,
  // eslint-disable-next-line no-unused-vars
  closePopup,
}) {
  const [loading, setLoading] = useState(false);
  const imageWrapperRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  // Safely read title and cover ID:
  const title = article.post_container?.post_title || "Untitled";
  const coverId = article.post_container?.post_cover?.image_id || "";

  // Called when user clicks the “+” or “✅” icon:
  async function handleIconClick(e) {
    e.stopPropagation();
    setLoading(true);
    try {
      await onToggle(article._id);
    } catch (err) {
      console.error("❌ [ArticleRow] toggle error:", err);
    }
    setLoading(false);
  }

  return (
    <div className={`article-row ${isSelected ? "selected" : ""}`}>
      {/* ─── LEFT: Cover / Placeholder ───────────────────────────────────────────── */}
      <div className="article-row__media">
        {coverId ? (
          <img
            className="article-row__cover"
            src={`${config.app.imageServiceDomain}/${coverId}/w=100`}
            alt="Cover"
            ref={imageWrapperRef}
          />
        ) : (
          <div
            className="article-row__placeholder"
            ref={imageWrapperRef}
          />
        )}
      </div>
      {/* ─── MIDDLE: Title (italic + ellipsis).  Add subtitle/author if you want ───────── */}
      <div className="article-row__info">
        <Text className="article-row__title font-size-medium" paragraph>
          {title}
        </Text>
        {/*
        <Text className="article-row__subtitle" paragraph small>
          by {article.gallery?.user_username || "Unknown"}
        </Text>
        */}
      </div>
      {/* ─── RIGHT: Spinner / Check / Plus ───────────────────────────────────────────── */}
      <div
        className="article-row__action"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <Loader inline className="article-row__spinner" thin />
        ) : isSelected ? (
          <PublicSuccessCheck
            className="article-row__icon article-row__icon--added"
            onClick={handleIconClick}
          />
        ) : (
          <AddIcon
            className="article-row__icon article-row__icon--add"
            onClick={handleIconClick}
          />
        )}
      </div>
    </div>
  );
}
